// Lightweight haptics helper.
//
// Reads the user's per-event toggles from localStorage and degrades gracefully:
//   • iOS / Android with @capacitor/haptics           → Taptic Engine via plugin
//   • Android Chrome / web with Vibration API         → navigator.vibrate
//   • iOS Safari / unsupported                        → silent no-op
//
// @capacitor/haptics is now a declared dependency (package.json). We use a
// plain dynamic import so webpack code-splits and bundles it — the earlier
// webpackIgnore trick never let the plugin reach the native bundle, which is
// why TestFlight users felt nothing.

import { isNative } from "./capacitor";

let capModule = null;
let capLoadAttempted = false;
async function loadCap() {
  if (capLoadAttempted) return capModule;
  capLoadAttempted = true;
  try {
    capModule = await import("@capacitor/haptics");
  } catch {
    capModule = null;
  }
  return capModule;
}

// Warm the module on first render on native so the first tap doesn't pay the
// code-split latency cost.
if (typeof window !== "undefined" && isNative()) {
  loadCap();
}

const READ = (key, fallback = true) => {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return v !== "false";
  } catch { return fallback; }
};

const masterOn = () => READ("hapticsEnabled", true);

function vibrateMs(pattern) {
  try { if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(pattern); } catch {}
}

export async function hapticImpact(style = "light") {
  if (!masterOn()) return;
  if (isNative()) {
    const cap = await loadCap();
    if (cap?.Haptics?.impact) {
      try {
        const styleKey = style.charAt(0).toUpperCase() + style.slice(1);
        await cap.Haptics.impact({ style: cap.ImpactStyle?.[styleKey] || cap.ImpactStyle?.Light });
        return;
      } catch {}
    }
  }
  vibrateMs(style === "heavy" ? 30 : style === "medium" ? 18 : 8);
}

export async function hapticSelection() {
  if (!masterOn()) return;
  if (isNative()) {
    const cap = await loadCap();
    if (cap?.Haptics?.selectionStart) {
      try {
        await cap.Haptics.selectionStart();
        await cap.Haptics.selectionChanged();
        await cap.Haptics.selectionEnd();
        return;
      } catch {}
    }
    // Fall back to light impact if selection API unavailable
    if (cap?.Haptics?.impact) {
      try { await cap.Haptics.impact({ style: cap.ImpactStyle?.Light }); return; } catch {}
    }
  }
  vibrateMs(10);
}

export async function hapticNotify(type = "success") {
  if (!masterOn()) return;
  if (isNative()) {
    const cap = await loadCap();
    if (cap?.Haptics?.notification) {
      try {
        const typeKey = type.charAt(0).toUpperCase() + type.slice(1);
        await cap.Haptics.notification({ type: cap.NotificationType?.[typeKey] || cap.NotificationType?.Success });
        return;
      } catch {}
    }
  }
  vibrateMs(type === "error" ? [40, 30, 40] : type === "warning" ? [25, 25, 25] : [12, 18, 12]);
}

// Per-event helpers — checked against their own toggle in addition to the master.
export const hapticTasbeeh = (i) => {
  if (!READ("hapticsTasbeeh", true)) return;
  if (i > 0 && i % 100 === 0) return hapticImpact("heavy");
  if (i > 0 && i % 33 === 0) return hapticImpact("medium");
  return hapticImpact("light");
};

export const hapticPrayerMarked = () => {
  if (!READ("hapticsPrayerMark", true)) return;
  return hapticNotify("success");
};

export const hapticPrayerComplete = () => {
  if (!READ("hapticsPrayerMark", true)) return;
  hapticImpact("light");
  setTimeout(() => hapticImpact("light"), 90);
  setTimeout(() => hapticImpact("light"), 180);
};

export const hapticTabSelection = () => {
  if (!READ("hapticsTabSelection", true)) return;
  return hapticSelection();
};

export const hapticUiSelection = () => {
  if (!READ("hapticsTabSelection", true)) return;
  return hapticSelection();
};

// Audio controls — play/pause on radio, surah, azaan preview
export const hapticPlayPause = () => hapticImpact("light");

// Any on/off toggle or list-style selection — bell, add/remove, hijri, reciter, speed, PTR
export const hapticToggle = () => hapticSelection();

// Moments of completion / celebration — live azaan starts, payment success, will signed,
// witness signed, fasting day marked, onboarding complete
export const hapticSuccess = () => hapticNotify("success");

// Long-press recognised (e.g. long-press on masjid card to unfollow)
export const hapticLongPress = () => hapticImpact("medium");

// Swipe-back gesture commits (threshold crossed)
export const hapticSwipeCommit = () => hapticImpact("light");

// Onboarding ceremony — user's name fades in
export const hapticCeremony = () => hapticImpact("heavy");

export const hapticQiblaAligned = () => {
  try {
    if (localStorage.getItem("qiblaHaptic") === "false") return;
    if (!masterOn()) return;
  } catch {}
  return hapticImpact("light");
};
